import { HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { handleRefreshSelectedMLCreatorTable } from '../store/mlcreator-slice/mlcreator';
import { handleRefreshOptimizationsTable } from '../store/optimization-slice/optimization';
import {
    fetchSubscriptionBillingState,
    fetchSubscriptionPlans,
    setSubscriptionSyncPending,
} from '../store/subscription-slice/subscription-slice';
import { fetchTierPlans } from '../store/tiermanagement-slice/tiermanagement-slice';

const SUBSCRIPTION_UPSERT_EVENT = 'stripe.subscription.upsert';
const SUBSCRIPTION_DELETED_EVENT = 'stripe.subscription.deleted';
const SUBSCRIPTION_SCHEDULE_UPDATED_EVENT = 'stripe.subscription.schedule.updated';
const SUBSCRIPTION_SCHEDULE_RELEASED_OR_CANCELED_EVENT = 'stripe.subscription.schedule.released_or_canceled';
const STRIPE_PRODUCT_UPDATED_EVENT = 'stripe.product.updated';

type SubscriptionUpdatePayload = {
    user?: string;
    content?: string;
} | null;

type SubscriptionUpdateListener = (payload: SubscriptionUpdatePayload) => void;

class SignalRService {
    private connection: HubConnection | null = null;
    private subscriptionUpdateListeners = new Set<SubscriptionUpdateListener>();

    public async startConnection(dispatch: any, token: string): Promise<void> {
        if (this.connection) {
            return;
        }

        const hubUrl = `${process.env.REACT_APP_API_URL}/notificationHub`;
        this.connection = new HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => token,
                skipNegotiation: false,
                withCredentials: false,
                transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
            })
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            .build();

        try {
            await this.connection.start();
            console.log("SignalR Connected.");
            this.setupListeners(dispatch);
        } catch (error) {
            console.error("Error starting SignalR connection: ", error);
        }

        this.connection.onclose((error) => {
            console.log("SignalR Disconnected.", error);
        });
    }

    public async stopConnection(): Promise<void> {
        if (this.connection) {
            await this.connection.stop();
            this.connection = null;
            console.log("SignalR Disconnected.");
        }
    }

    public subscribeToSubscriptionUpdates(listener: SubscriptionUpdateListener): () => void {
        this.subscriptionUpdateListeners.add(listener);
        return () => {
            this.subscriptionUpdateListeners.delete(listener);
        };
    }
    
    private setupListeners(dispatch: any): void {
        if (this.connection) {
            this.connection.off("TrainingUpdate");
            this.connection.off("OptimisationUpdate");
            this.connection.off("SubscriptionUpdate");

            this.connection.on("TrainingUpdate", () => {
                dispatch(handleRefreshSelectedMLCreatorTable()); 
            });

            this.connection.on("OptimisationUpdate", () => {
                dispatch(handleRefreshOptimizationsTable()); 
            });

            this.connection.on("SubscriptionUpdate", (message: SubscriptionUpdatePayload) => {
                const content = (message?.content || '').trim().toLowerCase();

                if (
                    content === SUBSCRIPTION_UPSERT_EVENT ||
                    content === SUBSCRIPTION_DELETED_EVENT ||
                    content === SUBSCRIPTION_SCHEDULE_UPDATED_EVENT ||
                    content === SUBSCRIPTION_SCHEDULE_RELEASED_OR_CANCELED_EVENT ||
                    content === STRIPE_PRODUCT_UPDATED_EVENT
                ) {
                    dispatch(setSubscriptionSyncPending(false));
                }

                if (content === SUBSCRIPTION_UPSERT_EVENT) {
                    void dispatch(fetchSubscriptionBillingState({ showLoader: false }));
                    void dispatch(fetchSubscriptionPlans({ showLoader: false }));
                } else if (content === SUBSCRIPTION_DELETED_EVENT) {
                    void dispatch(fetchSubscriptionBillingState({ showLoader: false }));
                } else if (
                    content === SUBSCRIPTION_SCHEDULE_UPDATED_EVENT ||
                    content === SUBSCRIPTION_SCHEDULE_RELEASED_OR_CANCELED_EVENT
                ) {
                    void dispatch(fetchSubscriptionBillingState({ showLoader: false }));
                } else if (content === STRIPE_PRODUCT_UPDATED_EVENT) {
                    void dispatch(fetchSubscriptionPlans({ showLoader: false }));
                    void dispatch(fetchTierPlans());
                }

                this.subscriptionUpdateListeners.forEach((listener) => {
                    try {
                        listener(message);
                    } catch (error) {
                        console.error("Error in subscription update listener: ", error);
                    }
                });
            });
        }
    }
}

const signalRService = new SignalRService();
export default signalRService;
